<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <div>
      <label for="org-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >Organization Name</label
      >
      <input
        id="org-name"
        v-model="form.name"
        type="text"
        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        required
      />
      <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
    </div>

    <Checkbox
      v-model="form.jit_enabled"
      label="Enable JIT (Just-In-Time) provisioning"
      :disabled="disabledJit"
      :hint="disabledJit ? 'Only organization owners can enable JIT provisioning.' : undefined"
    />

    <div class="flex justify-end space-x-3">
      <Button v-if="showCancel" type="button" variant="outline" @click="$emit('cancel')">
        Cancel
      </Button>
      <Button type="submit" :loading="isLoading" :disabled="!isDirty && !isNew">
        {{ isNew ? 'Create Organization' : 'Save Changes' }}
      </Button>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { computed, watch, reactive } from 'vue';
  import type {
    Organization,
    CreateOrganizationRequest,
    UpdateOrganizationRequest,
  } from '../types';
  import { createOrganizationSchema, updateOrganizationSchema } from '../validation';
  import { z } from 'zod';
  import { Button, Checkbox } from '@asabdev/asab-ui';

  interface Props {
    organization?: Organization | null;
    isLoading?: boolean;
    disabledJit?: boolean;
    showCancel?: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'submit', data: CreateOrganizationRequest | UpdateOrganizationRequest): void;
    (e: 'cancel'): void;
  }>();

  const isNew = computed(() => !props.organization);
  const errors = reactive<Record<string, string>>({});

  const form = reactive({
    name: props.organization?.name || '',
    jit_enabled: !!props.organization?.jit_enabled,
  });

  const isDirty = computed(() => {
    if (isNew.value) return true;
    return (
      form.name !== props.organization?.name || form.jit_enabled !== props.organization?.jit_enabled
    );
  });

  watch(
    () => props.organization,
    newOrg => {
      if (newOrg) {
        form.name = newOrg.name || '';
        form.jit_enabled = !!newOrg.jit_enabled;
      }
    },
    { deep: true }
  );

  const handleSubmit = () => {
    try {
      Object.keys(errors).forEach(key => delete errors[key]);

      if (isNew.value) {
        const data: CreateOrganizationRequest = {
          name: form.name,
        };
        createOrganizationSchema.parse(data);
        emit('submit', data);
      } else {
        const data: UpdateOrganizationRequest = {
          name: form.name,
          jit_enabled: form.jit_enabled,
        };
        updateOrganizationSchema.parse(data);
        emit('submit', data);
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        err.errors.forEach(e => {
          if (e.path[0]) {
            errors[e.path[0] as string] = e.message;
          }
        });
      }
    }
  };
</script>
